import { Playbook, PlaybookCategory } from '@/types/playbook/playbook';
import { playbookCategories } from './categories';
import { supportPlaybooks } from './support';
import { leadCapturePlaybooks } from './lead-capture';
import { physicalWorldPlaybooks } from './physical-world';
import { onboardEducatePlaybooks } from './onboard-educate';
import { amplifyMarketingPlaybooks } from './amplify-marketing';
import { gatherInsightsPlaybooks } from './gather-insights';

// Central registry of all playbooks
const playbookRegistry = new Map<string, Playbook>();

// Register all playbooks
[
  ...supportPlaybooks, 
  ...leadCapturePlaybooks,
  ...physicalWorldPlaybooks,
  ...onboardEducatePlaybooks,
  ...amplifyMarketingPlaybooks,
  ...gatherInsightsPlaybooks
].forEach(playbook => {
  playbookRegistry.set(playbook.meta.id, playbook);
});

// Utility functions for accessing playbooks
export const getPlaybookById = (id: string): Playbook | undefined => {
  return playbookRegistry.get(id);
};

export const getPlaybookBySlug = (slug: string): Playbook | undefined => {
  return Array.from(playbookRegistry.values()).find(p => p.meta.slug === slug);
};

export const getPlaybooksByCategory = (categoryId: string): Playbook[] => {
  return Array.from(playbookRegistry.values()).filter(p => p.meta.categoryId === categoryId);
};

export const getAllPlaybooks = (): Playbook[] => {
  return Array.from(playbookRegistry.values());
};

export const getFeaturedPlaybooks = (): Playbook[] => {
  return Array.from(playbookRegistry.values()).filter(p => p.meta.isFeatured);
};

export const getRelatedPlaybooks = (playbookId: string, limit = 3): Playbook[] => {
  const playbook = getPlaybookById(playbookId);
  if (!playbook) return [];

  const related: Playbook[] = [];
  const allPlaybooks = getAllPlaybooks();
  
  // First, get explicitly related playbooks
  if (playbook.meta.relatedPlaybookIds) {
    playbook.meta.relatedPlaybookIds.forEach(id => {
      const relatedPlaybook = getPlaybookById(id);
      if (relatedPlaybook) related.push(relatedPlaybook);
    });
  }
  
  // Then, get playbooks from same category (but not already added)
  if (related.length < limit) {
    const categoryPlaybooks = getPlaybooksByCategory(playbook.meta.categoryId)
      .filter(p => p.meta.id !== playbookId && !related.some(r => r.meta.id === p.meta.id));
    
    // Shuffle category playbooks for variety
    const shuffled = categoryPlaybooks.sort(() => Math.random() - 0.5);
    related.push(...shuffled.slice(0, Math.min(2, limit - related.length)));
  }
  
  // Finally, get popular/featured playbooks from other categories for diversity
  if (related.length < limit) {
    const otherPlaybooks = allPlaybooks
      .filter(p => 
        p.meta.id !== playbookId && 
        p.meta.categoryId !== playbook.meta.categoryId &&
        !related.some(r => r.meta.id === p.meta.id) &&
        p.meta.isFeatured // Prioritize featured playbooks
      )
      .sort(() => Math.random() - 0.5); // Randomize for variety
    
    related.push(...otherPlaybooks.slice(0, limit - related.length));
  }
  
  // If still not enough, just get any other playbooks
  if (related.length < limit) {
    const remainingPlaybooks = allPlaybooks
      .filter(p => 
        p.meta.id !== playbookId && 
        !related.some(r => r.meta.id === p.meta.id)
      )
      .sort(() => Math.random() - 0.5);
    
    related.push(...remainingPlaybooks.slice(0, limit - related.length));
  }
  
  return related.slice(0, limit);
};

export const searchPlaybooks = (query: string): Playbook[] => {
  const lowerQuery = query.toLowerCase();
  return Array.from(playbookRegistry.values()).filter(playbook => {
    const meta = playbook.meta;
    return (
      meta.name.toLowerCase().includes(lowerQuery) ||
      meta.tagline.toLowerCase().includes(lowerQuery) ||
      meta.description.toLowerCase().includes(lowerQuery) ||
      meta.tags?.some(tag => tag.toLowerCase().includes(lowerQuery))
    );
  });
};

export const getPlaybookCategories = (): PlaybookCategory[] => {
  return playbookCategories;
};

export const getCategoryById = (id: string): PlaybookCategory | undefined => {
  return playbookCategories.find(cat => cat.id === id);
};

export const getCategoryBySlug = (slug: string): PlaybookCategory | undefined => {
  return playbookCategories.find(cat => cat.slug === slug);
};

// Export the registry for advanced use cases
export { playbookRegistry };